<template>
  <div class="display-cfg">

    <header class="masthead">
      <h1 class="title">Display<span class="dot">.</span></h1>
      <p class="sub">Countries the hallway screen rotates through.</p>
    </header>

    <p v-if="loading" class="msg">Reading countries</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="countries.length === 0" class="msg">
      No countries recorded in Entra yet
    </p>

    <template v-else>
      <ul class="list">
        <li v-for="c in countries" :key="c.code" class="row" :class="{ off: !enabled(c.code) }">
          <span class="chip">{{ c.code }}</span>

          <span class="name">
            {{ c.name }}
            <span class="meta">
              {{ officeCount(c) }} {{ officeCount(c) === 1 ? 'location' : 'locations' }} &middot;
              {{ headcount(c) }} {{ headcount(c) === 1 ? 'person' : 'people' }}
            </span>
          </span>

          <label class="seconds">
            <input
              class="seconds-input"
              type="number"
              min="3"
              max="600"
              :value="settingFor(c.code).rotateSeconds"
              @change="setSeconds(c.code, $event.target.value)"
            >
            <span class="seconds-label">sec</span>
          </label>

          <!-- Arranging is per country, so the button belongs on the country
               rather than anywhere central. -->
          <router-link
            class="arrange-link"
            :to="{ name: 'ArrangeDisplay', params: { country: c.code } }"
          >Arrange</router-link>

          <label class="toggle">
            <input
              type="checkbox"
              :checked="enabled(c.code)"
              @change="toggle(c.code, $event.target.checked)"
            >
            <span class="track"><span class="knob"></span></span>
            <span class="state">{{ enabled(c.code) ? 'Shown' : 'Hidden' }}</span>
          </label>
        </li>
      </ul>

      <!-- Switching everything off leaves the wall with nothing to rotate, which
           looks like a fault rather than a setting. Worth saying so before
           someone spends an afternoon on it. -->
      <p v-if="shownCount === 0" class="warn">
        Nothing is shown. The display will sit empty until at least one country
        is switched back on.
      </p>

      <p v-if="saveError" class="msg err">{{ saveError }}</p>
    </template>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSiteHierarchy } from '@/services/GraphService'
import { getDisplayCountries, setDisplayCountry, isEnabled } from '@/services/DisplayService'
import { getLocations } from '@/services/OfficeColourService'

const countries = ref([])
const locations = ref([])
const settings = ref({})
const loading = ref(true)
const error = ref(null)
const saveError = ref(null)

function enabled(code) {
  return isEnabled(settings.value, code)
}

const shownCount = computed(() =>
  countries.value.filter((c) => enabled(c.code)).length
)

/**
 * Everyone with this usageLocation, not only those with an office set.
 *
 * The previous count summed the per-office headcounts, so anybody whose Entra
 * officeLocation was blank was invisible. That field is set on a handful of
 * accounts, so Denmark read "1 person" when it has dozens.
 */
function headcount(country) {
  return country.sites.reduce((sum, s) => sum + (s.headcount ?? 0), 0)
    + (country.peopleWithoutSite ?? 0)
}

/**
 * Locations in this country, from Vero's own list.
 *
 * It used to count Entra offices, which is a different thing entirely: a
 * location created under Admin > Locations never appeared, and an Entra office
 * nobody had configured in Vero did.
 */
function officeCount(country) {
  return locations.value.filter((l) => l.country === country.code).length
}


async function load() {
  loading.value = true
  error.value = null

  try {
    // Countries and headcounts come from Entra; the on/off state and the
    // locations from Vero.
    const [hierarchy, map, locationList] = await Promise.all([
      getSiteHierarchy(),
      getDisplayCountries(),
      // Not fatal: without it the location count reads zero, which is wrong but
      // does not stop anyone turning a country on or off, which is what this
      // page is for.
      getLocations().catch(() => [])
    ])

    countries.value = hierarchy?.countries ?? []
    settings.value = map
    locations.value = locationList
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

/**
 * Write both settings for a country.
 *
 * They live on one row and are edited on one line, so they are saved together:
 * sending only the changed one would need the endpoint to know which fields
 * were meant, and "the whole setting" is easier to be sure of.
 *
 * Applied immediately then saved; a control that waits for a round trip feels
 * broken. Reverted below if the save fails.
 */
async function write(code, next) {
  saveError.value = null

  const previous = settings.value[code]
  settings.value = { ...settings.value, [code]: next }

  try {
    await setDisplayCountry(code, next.enabled, next.rotateSeconds)
  } catch (err) {
    console.error(err)
    saveError.value = err.message ?? String(err)

    const reverted = { ...settings.value }
    if (previous === undefined) delete reverted[code]
    else reverted[code] = previous
    settings.value = reverted
  }
}

function settingFor(code) {
  return settings.value[code] ?? { enabled: true, rotateSeconds: 30 }
}

async function toggle(code, value) {
  await write(code, { ...settingFor(code), enabled: value })
}

async function setSeconds(code, raw) {
  const seconds = Number(raw)
  // Silently ignore nonsense rather than saving it: the input is a number field
  // but a person can still empty it, and an empty box is not a request to
  // change anything.
  if (!Number.isFinite(seconds) || seconds < 3 || seconds > 600) return

  await write(code, { ...settingFor(code), rotateSeconds: seconds })
}

onMounted(load)
</script>


<style scoped>
.display-cfg {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --on: #0f9d58;

  /* Left aligned, like every other page. Centring made the content drift right
     of the menu and the header logo, so nothing lined up with anything else. */
  max-width: 780px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 8px;
}

.title {
  font-size: clamp(30px, 4.2vw, 44px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  color: var(--ink-soft);
  margin: 8px 0 0;
}

/* ---------- List ---------- */

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
  border-bottom: 1px solid var(--line);
}

.row:last-child { border-bottom: none; }

.chip {
  flex: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: #fff;
  padding: 5px 10px;
  border-radius: 5px;
}

/* A hidden country is dimmed rather than removed, so the list is still the
   complete set and the state is obvious at a glance. */
.row.off .chip { background: #aab4c0; }
.row.off .name { color: #9aa5b2; }

.name {
  flex: 1;
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  min-width: 0;
}

.meta {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-soft);
  margin-top: 3px;
}

/* ---------- Toggle ---------- */

.seconds {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 16px;
}

.seconds-input {
  font-family: inherit;
  font-size: 14px;
  width: 68px;
  height: 38px;
  padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  color: #191c1e;
  outline: none;
  text-align: right;
}

.seconds-input:focus { border-color: var(--ink); }

.seconds-label {
  font-size: 13px;
  color: var(--ink-soft);
}

.arrange-link {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 8px 14px;
  margin-right: 16px;
  white-space: nowrap;
}

.arrange-link:hover {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

.toggle {
  display: flex;
  align-items: center;
  gap: 11px;
  cursor: pointer;
  flex: none;
}

.toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.track {
  width: 42px;
  height: 24px;
  border-radius: 12px;
  background: #cbd5dd;
  position: relative;
  transition: background 0.18s ease;
}

.knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 0.18s ease;
}

.toggle input:checked + .track { background: var(--on); }
.toggle input:checked + .track .knob { transform: translateX(18px); }

.toggle input:focus-visible + .track {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.state {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-soft);
  width: 52px;
}

/* ---------- Messages ---------- */

/* Grey, like every other message on the page. It said something worth saying --
   a board with every country switched off looks like a fault -- but the sentence
   does that work; the colour was only shouting it. */
.warn {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 20px 0 0;
}

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 10px;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 620px) {
  .display-cfg { padding: 0 20px 60px; }
  .state { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .track, .knob { transition: none; }
}
</style>
